import { ItemInstance, TreeInstance } from '@headless-tree/core';
import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import React from 'react';
import { GoalListCard } from 'shared/components/ui/goal-list-card';
import { InternalTreeItem } from 'shared/components/ui/virtualized-tree-list';
import { Goal } from 'shared/types/goal';
import { ContextMenuOptions } from 'shared/views/goal-filter-list/types';

import { useContextMenu } from '../../context-menu-context';
import * as Styled from './tree-goal-card.style';

type TreeGoalCardProps = {
  goal: Goal;
  item: ItemInstance<InternalTreeItem>;
  tree: TreeInstance<InternalTreeItem>;
  isMultiSelectModifierPressed?: boolean;
  onUpdateProgress: (id: string) => void;
  hideLifeArea?: boolean;
  excludedContextMenuOptions?: ContextMenuOptions[];
};

export const TreeGoalCard: React.FC<TreeGoalCardProps> = ({
  goal,
  item,
  tree,
  isMultiSelectModifierPressed,
  onUpdateProgress,
  hideLifeArea,
  excludedContextMenuOptions,
}) => {
  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas({ activeOnly: true });
  const hasChildren = !!item.getItemData().children?.length;
  const isExpanded = item.isExpanded();
  const preventInteraction = isMultiSelectModifierPressed;

  const { open } = useContextMenu();

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isExpanded) {
      item.collapse();
    } else {
      item.expand();
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const goalsMap = new Map(goals.map((g) => [g.id, g]));
    const selectedItems = [...tree.getSelectedItems()];
    const selectedGoals = selectedItems
      .map((i) => goalsMap.get(i.getItemData().sourceId))
      .filter(Boolean) as Goal[];

    if (!item.isSelected()) {
      tree.setSelectedItems([item.getId()]);
      selectedGoals.length = 0;
      selectedGoals.push(goal);
    }

    open(
      { left: e.clientX, top: e.clientY },
      {
        goals: selectedGoals,
      },
    );
  };

  const isMultiSelected =
    item.isSelected() && tree.getSelectedItems().length > 1;

  return (
    <Styled.Container onContextMenu={handleContextMenu}>
      <Styled.CardWrapper>
        <GoalListCard
          goal={goal}
          goals={goals}
          lifeAreas={lifeAreas}
          hideLifeArea={hideLifeArea}
          hasChildren={hasChildren}
          isExpanded={isExpanded}
          onToggleExpand={handleToggle}
          isSelected={item.isSelected()}
          isDropTarget={item.isDragTarget()}
          preventInteraction={preventInteraction}
          onUpdateProgress={onUpdateProgress}
          hideContextMenu={isMultiSelected || isMultiSelectModifierPressed}
          excludedContextMenuOptions={excludedContextMenuOptions}
        />
      </Styled.CardWrapper>
    </Styled.Container>
  );
};
